import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";
import {
  Box,
  Chip,
  CircularProgress,
  Slider,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import AppButton from "../../components/AppButton";

import { Column } from "../../Layout/utils";
import BacktestFiltersBar, { FilterState } from "./BacktestFilters";
import BookingCurveChart from "./BookingCurveChart";
import ChartRow from "./ChartRow";
import ErrorBreakdownChart from "./ErrorBreakdownChart";
import FeatureImportance from "./FeatureImportance";
import ForecastActivation from "./ForecastActivation";
import MeasureTiles from "./MeasureTiles";
import MetricsTable from "./MetricsTable";
import { formatterService } from "../../NumberFormatter";

import {
  ERROR_KINDS,
  ERROR_KIND_LABEL_KEYS,
  FEATURE_LABEL_KEYS,
  TARGET_ERROR_LABEL_KEYS,
  TARGET_LABEL_KEYS,
  TRAINING_STEP_LABEL_KEYS,
  money,
} from "./constants";
import {
  BacktestCurvePoint,
  BreakdownDimension,
  BreakdownRow,
  ErrorKind,
  ForecastTarget,
  fetchBacktest,
  fetchBacktestFilters,
  fetchForecastMetrics,
  trainForecastModels,
} from "./services";
import AppAlert from "../../components/AppAlert";

const POLL_INTERVAL_MS = 5000;

interface Breakdown {
  dimension: BreakdownDimension;
  title: string;
  /** Ordered and unevenly sampled: drawn as a line over a numeric axis, not as bars. */
  curve?: boolean;
  maxRows?: number;
  /** Dimensions whose keys come back raw ("2026-04", "1".."7") because only the browser
   *  knows the reader's locale. */
  naming?: "month" | "weekday";
}

/** The six error breakdowns, in reading order — ChartRow lays them out two per line. */
const BREAKDOWNS: Breakdown[] = [
  {
    dimension: "lead_time",
    title: "ML_FORECAST_LEAD_TIME_CHART_TITLE",
    curve: true,
  },
  { dimension: "modele_type", title: "ML_FORECAST_BREAKDOWN_BY_MODEL_TYPE" },
  {
    dimension: "hebergement",
    title: "ML_FORECAST_BREAKDOWN_BY_PROPERTY",
    maxRows: 20,
  },
  { dimension: "uh_type", title: "ML_FORECAST_BREAKDOWN_BY_UH_TYPE" },
  {
    dimension: "month",
    title: "ML_FORECAST_BREAKDOWN_BY_MONTH",
    naming: "month",
  },
  {
    dimension: "day_of_week",
    title: "ML_FORECAST_BREAKDOWN_BY_DAY_OF_WEEK",
    naming: "weekday",
  },
];

/** Local dates on purpose: "2026-04-01" parsed as a string is UTC and slips a month west of Greenwich. */
const monthDate = (key: string) =>
  new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1);

/** ISO weekday 1..7 named through a week that starts on a Monday. */
const weekdayDate = (key: string) => new Date(2024, 0, Number(key));

const EMPTY_FILTERS: FilterState = {
  hebergementIds: [],
  uhTypeIds: [],
  month: "",
  dateOccupation: "",
};

const MLForecastAnalysis = () => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  const queryClient = useQueryClient();
  const [selectedTarget, setSelectedTarget] = useState<ForecastTarget>("nb_uh");
  const [errorKind, setErrorKind] = useState<ErrorKind>("mae");
  // Null until moved, so a change of scope re-anchors it.
  const [fromLeadTime, setFromLeadTime] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const {
    data: metrics,
    isLoading,
    isError,
  } = useQuery("ml-forecast-metrics", fetchForecastMetrics, {
    refetchOnWindowFocus: false,
    refetchInterval: (data) =>
      data?.some((entry) =>
        ["pending", "running"].includes(entry.training_status)
      )
        ? POLL_INTERVAL_MS
        : false,
  });

  const isTraining = metrics?.some((entry) =>
    ["pending", "running"].includes(entry.training_status)
  );
  // A run empties the table it reads: keyed on the training timestamp, idle until it lands.
  const trainedAt = metrics?.[0]?.trained_at;
  const readable = metrics !== undefined && !isTraining;

  const { data: backtestFilters } = useQuery(
    ["ml-forecast-backtest-filters", trainedAt] as const,
    fetchBacktestFilters,
    {
      refetchOnWindowFocus: false,
      enabled: readable,
      keepPreviousData: true,
    }
  );

  const {
    data: backtest,
    isLoading: isLoadingBacktest,
    isFetching: isFetchingBacktest,
  } = useQuery(
    ["ml-forecast-backtest", filters, trainedAt] as const,
    () =>
      fetchBacktest({
        hebergementIds: filters.hebergementIds,
        uhTypeIds: filters.uhTypeIds,
        month: filters.month || undefined,
        dateOccupation: filters.dateOccupation || undefined,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: readable,
      keepPreviousData: true,
    }
  );
  const curve = backtest?.curve;
  // The measure is picked from what is already loaded, so switching tabs costs no request.
  const breakdown = backtest?.dimensions;

  // Clamped: a change of scope can leave the stored value out of range.
  const leadTimes = (curve ?? []).map((point) => point.lead_time);
  const earliest = Math.min(0, ...leadTimes);
  const latest = Math.max(earliest, ...leadTimes);
  const from = Math.min(Math.max(fromLeadTime ?? earliest, earliest), latest);
  const shownCurve = (curve ?? []).filter((point) => point.lead_time >= from);
  const framed = (rows: BreakdownRow[], entry: Breakdown) =>
    entry.curve ? rows.filter((row) => Number(row.key) >= from) : rows;

  // Only on a single stay date: over an aggregate the on-the-books price moves with the mix
  // of dates in the books, not with a price, and the sentence would read as a forecast defect.
  const priceMove = () => {
    const booked = shownCurve.filter((point) => point.rob_nb_uh > 0);
    const first = booked[0];
    const last = booked[booked.length - 1];
    if (!filters.dateOccupation || !first || !last || first === last)
      return undefined;
    // A forecast that produced nothing sums to zero nights, and a price needs nights: the
    // sentence is dropped rather than shown with a hole in it.
    const move = (prefix: "rob" | "baseline" | "ml") => {
      const at = (point: BacktestCurvePoint) => {
        const nights = point[`${prefix}_nb_uh`];
        return nights > 0 ? point[`${prefix}_revenue`] / nights : null;
      };
      const start = at(first);
      const end = at(last);
      if (start === null || end === null) return null;
      const delta = end - start;
      return `${delta > 0 ? "+" : ""}${money(formatter, delta)}`;
    };
    const onBooks = move("rob");
    const baseline = move("baseline");
    const ml = move("ml");
    if (onBooks === null || baseline === null || ml === null) return undefined;
    return intl.formatMessage(
      { id: "ML_FORECAST_PRICE_FOLLOW" },
      { onBooks, baseline, ml }
    );
  };

  const named = (rows: BreakdownRow[], naming?: Breakdown["naming"]) =>
    naming === undefined
      ? rows
      : rows.map((row) => ({
          ...row,
          label:
            naming === "month"
              ? intl.formatDate(monthDate(row.key), {
                  month: "short",
                  year: "numeric",
                })
              : intl.formatDate(weekdayDate(row.key), { weekday: "long" }),
        }));

  // Metrics only: the run has just been queued, the backtest table is about to be emptied.
  const trainMutation = useMutation(trainForecastModels, {
    onSuccess: () => queryClient.invalidateQueries("ml-forecast-metrics"),
  });

  const hasFailed = metrics?.some((entry) => entry.training_status === "error");
  const selected = metrics?.find((entry) => entry.target === selectedTarget);
  // Only a finished run leaves test rows behind.
  const hasResults = metrics?.some((entry) => entry.n_test > 0);
  const currentStep = metrics?.find(
    (entry) => entry.training_step
  )?.training_step;
  const stepKey = currentStep
    ? TRAINING_STEP_LABEL_KEYS[currentStep]
    : undefined;
  const stepLabel = stepKey ? ` — ${intl.formatMessage({ id: stepKey })}` : "";

  if (isLoading) {
    return (
      <Column sx={{ p: 2 }}>
        <CircularProgress size={24} />
      </Column>
    );
  }

  if (isError) {
    return (
      <Column sx={{ p: 2 }}>
        <AppAlert severity="error">
          {intl.formatMessage({ id: "ML_FORECAST_METRICS_LOAD_ERROR" })}
        </AppAlert>
      </Column>
    );
  }

  if (!metrics || !hasResults) {
    return (
      <Column sx={{ p: 2, gap: 2, maxWidth: "100%" }}>
        {isTraining ? (
          <AppAlert severity="info" icon={<CircularProgress size={18} />}>
            {intl.formatMessage(
              { id: "ML_FORECAST_TRAINING_FIRST_RUN" },
              { step: stepLabel }
            )}
          </AppAlert>
        ) : (
          <>
            <AppAlert severity={hasFailed ? "error" : "info"}>
              <Typography variant="body2">
                {intl.formatMessage({
                  id: hasFailed
                    ? "ML_FORECAST_TRAINING_FAILED_RETRY"
                    : "ML_FORECAST_NO_MODEL",
                })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {intl.formatMessage({ id: "ML_FORECAST_PREREQUISITES" })}
              </Typography>
            </AppAlert>
            <Box>
              <AppButton
                disabled={trainMutation.isLoading}
                onClick={() => trainMutation.mutate()}
              >
                {intl.formatMessage({ id: "ML_FORECAST_TRAIN_FIRST" })}
              </AppButton>
            </Box>
          </>
        )}
      </Column>
    );
  }

  // Both targets are trained in the same run and share their context, so either one describes it.
  const reference = metrics[0];
  const trainedOn = trainedAt ? intl.formatDate(trainedAt) : null;
  const leadTimeCount = reference?.lead_time_curve.length ?? 0;
  // `names` turns an item into a hover list: the count alone does not say what was fed in.
  const headline: { text: string; names?: string[] }[] = [
    ...(reference
      ? [
          {
            text: intl.formatMessage(
              { id: "ML_FORECAST_FEATURES_COUNT" },
              { count: reference.features.length }
            ),
            names: reference.features.map((feature) => {
              const key = FEATURE_LABEL_KEYS[feature];
              return key ? intl.formatMessage({ id: key }) : feature;
            }),
          },
        ]
      : []),
    ...(leadTimeCount > 0
      ? [
          {
            text: intl.formatMessage(
              { id: "ML_FORECAST_LEAD_TIME_TIERS" },
              { count: leadTimeCount }
            ),
          },
        ]
      : []),
    ...(trainedOn
      ? [
          {
            text: intl.formatMessage(
              { id: "ML_FORECAST_TRAINED_ON" },
              { date: trainedOn }
            ),
          },
        ]
      : []),
  ];

  return (
    <Column sx={{ p: 2, gap: 2, maxWidth: "100%", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ borderLeft: 3, borderColor: "primary.main", pl: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "ML_FORECAST_TITLE" })}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mt: 0.5,
            }}
          >
            {headline.map((item, index) => (
              <Box
                key={item.text}
                sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
              >
                {index > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.disabled", mx: 0.75 }}
                  >
                    ·
                  </Typography>
                )}
                <Tooltip
                  title={
                    item.names ? (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          columnGap: 2,
                          rowGap: 0.5,
                        }}
                      >
                        {item.names.map((name) => (
                          <Typography key={name} variant="caption">
                            {name}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      ""
                    )
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      borderBottom: item.names ? "1px dotted" : undefined,
                      cursor: item.names ? "help" : undefined,
                    }}
                  >
                    {item.text}
                  </Typography>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
        <ForecastActivation />
      </Box>

      {isTraining && (
        <AppAlert severity="info" sx={{ py: 0.5 }}>
          {intl.formatMessage(
            { id: "ML_FORECAST_TRAINING_BANNER" },
            { step: stepLabel }
          )}
        </AppAlert>
      )}
      {hasFailed && (
        <AppAlert severity="error" sx={{ py: 0.5 }}>
          {intl.formatMessage({ id: "ML_FORECAST_TRAINING_FAILED" })}
        </AppAlert>
      )}
      {trainMutation.isError && (
        <AppAlert severity="error" sx={{ py: 0.5 }}>
          {intl.formatMessage({
            id:
              (trainMutation.error as Error)?.message === "409"
                ? "ML_FORECAST_TRAINING_ALREADY_RUNNING"
                : "ML_FORECAST_TRAINING_START_FAILED",
          })}
        </AppAlert>
      )}

      {backtestFilters && (
        <BacktestFiltersBar
          filters={backtestFilters}
          value={filters}
          onChange={setFilters}
        />
      )}

      {/* Data first, so a run in progress keeps the previous one on screen. */}
      {curve && curve.length > 0 ? (
        <>
          {/* The tiles keep the whole scope: the slider frames the charts only. */}
          <MeasureTiles points={curve} />
          <Box
            sx={{
              opacity: isFetchingBacktest ? 0.6 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 1,
                mb: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {intl.formatMessage({ id: "ML_FORECAST_FEATURE_LEAD_TIME" })}
              </Typography>
              <Slider
                size="small"
                min={earliest}
                max={latest}
                value={from}
                onChange={(_, value) =>
                  setFromLeadTime(Array.isArray(value) ? earliest : value)
                }
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `J${value}`}
                sx={{ maxWidth: 360 }}
              />
              <Typography variant="body2">{`J${from} → J${latest}`}</Typography>
            </Box>
            <ChartRow>
              <BookingCurveChart points={shownCurve} measure="nb_uh" />
              <BookingCurveChart
                points={shownCurve}
                measure="adr"
                note={priceMove()}
              />
            </ChartRow>
          </Box>
        </>
      ) : isLoadingBacktest || isTraining ? (
        <CircularProgress size={24} />
      ) : (
        <AppAlert severity="info">
          {intl.formatMessage({ id: "ML_FORECAST_NO_DATA_IN_SCOPE" })}
        </AppAlert>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {metrics.map((entry) => (
            <Chip
              key={entry.target}
              size="small"
              label={intl.formatMessage({
                id: TARGET_LABEL_KEYS[entry.target],
              })}
              onClick={() => setSelectedTarget(entry.target)}
              variant={selectedTarget === entry.target ? "filled" : "outlined"}
              color={selectedTarget === entry.target ? "primary" : "default"}
            />
          ))}
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {ERROR_KINDS.map((kind) => (
            <Chip
              key={kind}
              size="small"
              label={intl.formatMessage({ id: ERROR_KIND_LABEL_KEYS[kind] })}
              onClick={() => setErrorKind(kind)}
              variant={errorKind === kind ? "filled" : "outlined"}
              color={errorKind === kind ? "primary" : "default"}
            />
          ))}
        </Box>
      </Box>

      {breakdown && (
        <ChartRow>
          {BREAKDOWNS.map((entry) => (
            <ErrorBreakdownChart
              key={entry.dimension}
              title={intl.formatMessage(
                { id: entry.title },
                {
                  measure: intl.formatMessage({
                    id: TARGET_ERROR_LABEL_KEYS[selectedTarget],
                  }),
                }
              )}
              rows={named(
                framed(breakdown[entry.dimension][selectedTarget], entry),
                entry.naming
              )}
              target={selectedTarget}
              kind={errorKind}
              maxRows={entry.maxRows}
              curve={entry.curve}
            />
          ))}
        </ChartRow>
      )}

      <ChartRow>
        {selected && <FeatureImportance metrics={selected} />}
        <MetricsTable metrics={metrics} />
      </ChartRow>
    </Column>
  );
};

export default MLForecastAnalysis;
